import { Dispatch, SetStateAction } from 'react'
import {
  Button,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import { DataList } from '@/types/data-lists'
import { Organization } from '@/types/organizations'
import { useCreateDataListWithDefaults } from '@/service-library/hooks/data-lists'
import useIsSuperUser from '@/services/hooks/useIsSuperUser'
import { SUPER_USER_ONLY_COLOR } from '@/theme/usePixydocsTheme'
import generateUuid from '@/utils/uuid'
import { showErrorSnackbar } from '@/utils/snackbars'
import { useDemoModeContext } from '@/components/demo-mode-provider/DemoModeProvider'

type ListIsInheritingMessageProps = {
  parentOrg: Organization
  dataList: DataList
  rootDataList: DataList
  selectedOrg: Organization | null
  setSelectedOrg: Dispatch<SetStateAction<Organization | null>>
  detailQueryKey: QueryKey
}

export default function ListIsInheritingMessage({
  dataList,
  rootDataList,
  parentOrg,
  selectedOrg,
  setSelectedOrg,
  detailQueryKey,
}: ListIsInheritingMessageProps) {
  const isSuperUser = useIsSuperUser()
  const [demoMode] = useDemoModeContext()

  const { createDataList, isPending } = useCreateDataListWithDefaults({
    sideEffectQueryKeys: [detailQueryKey],
    onError: () => {
      showErrorSnackbar('Unable to create list. Please try again later.')
    },
  })

  function handleCreateSeparateDataList() {
    createDataList({
      ...dataList,
      id: generateUuid(),
      name: `${dataList.name} (${selectedOrg?.name})`,
      parent_data_list_id: rootDataList.id,
      org_id: selectedOrg?.id as string,
    })
  }

  return parentOrg ? (
    <Stack alignItems="center" sx={{ py: 4, flexGrow: 1 }} spacing={2}>
      <Typography>
        This organization is inheriting its list data from{' '}
        <Link
          onClick={() => {
            setSelectedOrg(parentOrg)
          }}
        >
          {parentOrg?.name}
        </Link>
        .
      </Typography>
      {((isSuperUser && !demoMode) ||
        rootDataList.is_child_list_creation_allowed_in_ui) && (
        <Button
          variant="text"
          onClick={handleCreateSeparateDataList}
          disabled={isPending}
          startIcon={isPending ? <CircularProgress size={20} /> : undefined}
          sx={{
            color: rootDataList.is_child_list_creation_allowed_in_ui
              ? undefined
              : SUPER_USER_ONLY_COLOR,
          }}
        >
          Create Separate Data for this Organization
        </Button>
      )}
    </Stack>
  ) : null
}
